import React, { useEffect } from "react";
import {
    FlexContainer,
    LottieLoadingIndicator,
    StandardModal,
    StandardModalActionGroup,
    StandardModalDescription,
    StandardModalFormGroup,
    StandardModalSection,
    StandardModalSectionTitle,
    Toast
} from "src/components";
import FormField from "src/components/FormField";
import {
    SaveButton, CancelButton, 
    SpinnerContainer, RadioButton, 
    CheckMark, SquareBox
} from "../style";
import { getCategory } from "../api";
import { useSaveCategory } from "../hooks";

const SvgCheckMark = (
    <svg width="20" height="60" viewBox="0 0 14 12" fill="none">
        <path d="M3 7L6 10L11 4" stroke="white" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const ManageQuotesModal = (props) => {
    const { show, onClose, user, companyUuid } = props;
    const { 
        setInput,
        isSubmitting, 
        categories,
        handleCategory, 
        handleSubmit,
        setIsSubmitting,
        setCategories
    } = 
        useSaveCategory({ companyUuid, close: onClose });
    useEffect(() => {
        const loadCategory = async () => {
            setIsSubmitting(true);
            try {
                const response = await getCategory(user);
                setCategories({
                    companyUuid,
                    category: response.category || response.companyName,
                    isGeneral: response.isGeneral,
                    isPremium: response.isPremium,
                    uuid: response.uuid,
                    isUpdatable: response.isUpdatable,
                });
                setInput(response.category || response.companyName); // For empty field
            } catch {
                Toast.error("Error fetching quote category.");
            } finally {
                setIsSubmitting(false);
            }
        };

        if (user && companyUuid) loadCategory();
    }, [user, companyUuid]);

    return (
        <StandardModal
            open={show}
            onClose={onClose}
            widthMode="large"
            title="Manage & Customise Quote Categories"
            description="Control which Me Business quote categories are visible in your app and create your own custom category title to personalise your quote experience."
            footer={(
                <StandardModalActionGroup $pushRight>
                    { isSubmitting ? (
                        <SpinnerContainer>
                            <LottieLoadingIndicator height="1.563rem" width="1.563rem" containerHeight="1.563rem" margin="0" />
                        </SpinnerContainer>
                    ) : (
                        <>
                            <CancelButton disabled={isSubmitting} onClick={onClose} > Cancel </CancelButton>
                            <SaveButton disabled={isSubmitting} onClick={handleSubmit} backgroundColor="#171A22" color="#fff"
                                width="140px"
                                type="submit"
                                border="none"
                            > Save </SaveButton>
                        </>
                    )}
                </StandardModalActionGroup>
            )}
        >
            <StandardModalSection>
                <StandardModalFormGroup>
                    <StandardModalSectionTitle>Create a Custom Category Title (Optional)</StandardModalSectionTitle>
                    <StandardModalDescription color="#6A7387">
                        This title will be used as the name for your business category for all your company specific quotes. If you don’t not enter a title we will use your business name as the category title.
                    </StandardModalDescription>
                    <FormField borderRadius="8px" size="14px" height="46px"
                        placeholderColor="#808080" border="1px solid #C9D2E7"
                        backgroundColor={"#F3F5FB"}
                        boxShadow="none"
                        opacity="1"
                        align="left"
                        width="min(100%, 364px)"
                        type="text"
                        placeholder={"e.g., Company Quotes"}
                        onChange={(e) => handleCategory("categoryTitle", e)}
                        showErrors={true}
                        maxLength="100"
                        value={categories.category}
                    />
                </StandardModalFormGroup>
            </StandardModalSection>
            <StandardModalSection>
                <StandardModalFormGroup>
                    <StandardModalSectionTitle>Show/Hide Me Business Categories</StandardModalSectionTitle>
                    <StandardModalDescription color="#6A7387">
                        Check or Uncheck categories to control which ones appear for your users in the app.
                    </StandardModalDescription>
                    <FlexContainer gap="0.6rem" alignItems="flex-start">
                        <RadioButton
                            active={categories.isGeneral}
                            onClick={() => handleCategory("isGeneral")}
                            type="button"
                        >
                            General Quotes
                            <SquareBox active={categories.isGeneral}>
                                {categories.isGeneral && 
                                    <CheckMark>{SvgCheckMark}</CheckMark>
                                }
                            </SquareBox>
                        </RadioButton>

                        <RadioButton
                            active={categories.isPremium}
                            onClick={() => handleCategory("isPremium")}
                            type="button"
                        >
                            Premium Quotes
                            <SquareBox active={categories.isPremium}>
                                {categories.isPremium && <CheckMark>{SvgCheckMark}</CheckMark>}
                            </SquareBox>
                        </RadioButton>
                    </FlexContainer>
                </StandardModalFormGroup>
            </StandardModalSection>
        </StandardModal>
    );
};

export default ManageQuotesModal;
